import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Customer, Questionnaire as QuestionnaireType, QuestionResponse } from '../types';
import apiClient from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import SectionCommentPopup from './SectionCommentPopup';

const LLM_API_URL = window.location.protocol === 'https:' ? '/api/llm' : `http://${window.location.hostname}:3010/api/llm`;

const Questionnaire: React.FC = () => {
  const navigate = useNavigate();
  const { customerId, questionnaireId, responseId } = useParams<{ customerId: string; questionnaireId?: string; responseId?: string }>();
  const location = useLocation();
  const { t } = useLanguage();
  const [customer, setCustomer] = useState<Customer | null>(location.state?.customer || null);

  const [questionnaire, setQuestionnaire] = useState<QuestionnaireType | null>(null);
  const [responses, setResponses] = useState<{ [key: string]: string }>({});
  const [expandedCategories, setExpandedCategories] = useState<{ [key: string]: boolean }>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);

  // AI assistance state
  const [aiLoading, setAiLoading] = useState<{ [key: string]: boolean }>({});
  const [aiSuggestions, setAiSuggestions] = useState<{ [key: string]: string[] }>({});
  const [suggestLoading, setSuggestLoading] = useState<{ [key: string]: boolean }>({});
  const suggestTimers = useRef<{ [key: string]: NodeJS.Timeout }>({});

  // Content language toggle (JP/EN for questions display)
  const [contentLang, setContentLang] = useState<'ja' | 'en'>(() => {
    try {
      const stored = localStorage.getItem('ea-qa-content-language');
      if (stored === 'en') return 'en';
    } catch {}
    return 'ja';
  });

  // Section comments state
  const [commentPopupCategory, setCommentPopupCategory] = useState<string | null>(null);
  const [commentCounts, setCommentCounts] = useState<{ [category: string]: number }>({});

  const handleAiRegenerate = useCallback(async (category: string, question: string, _defaultAnswer: string) => {
    const key = `${category}::${question}`;
    const currentText = responses[key];
    if (!currentText?.trim()) return;

    setAiLoading(prev => ({ ...prev, [key]: true }));
    try {
      const res = await fetch(`${LLM_API_URL}/regenerate-japanese`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: currentText }),
      });
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      if (data.japanese) {
        setResponses(prev => ({ ...prev, [key]: data.japanese }));
      }
    } catch {
      // silent fail
    } finally {
      setAiLoading(prev => ({ ...prev, [key]: false }));
    }
  }, [responses]);

  const fetchSuggestions = useCallback(async (key: string, question: string, defaultAnswer: string, currentInput: string) => {
    setSuggestLoading(prev => ({ ...prev, [key]: true }));
    try {
      const res = await fetch(`${LLM_API_URL}/suggest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question, defaultAnswer, currentInput }),
      });
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      if (data.suggestions && data.suggestions.length > 0) {
        setAiSuggestions(prev => ({ ...prev, [key]: data.suggestions.slice(0, 3) }));
      }
    } catch {
      // silent fail
    } finally {
      setSuggestLoading(prev => ({ ...prev, [key]: false }));
    }
  }, []);

  const handleAnswerChangeWithSuggestions = (category: string, question: string, defaultAnswer: string, value: string) => {
    const key = getQuestionKey(category, question);
    setResponses(prev => ({ ...prev, [key]: value }));

    // Debounce: fetch suggestions after 2s of no typing
    if (suggestTimers.current[key]) {
      clearTimeout(suggestTimers.current[key]);
    }
    if (value.trim().length >= 2) {
      suggestTimers.current[key] = setTimeout(() => {
        fetchSuggestions(key, question, defaultAnswer, value);
      }, 2000);
    } else {
      setAiSuggestions(prev => { const next = { ...prev }; delete next[key]; return next; });
    }
  };

  const applySuggestion = (category: string, question: string, suggestion: string) => {
    const key = getQuestionKey(category, question);
    setResponses(prev => ({ ...prev, [key]: suggestion }));
    setAiSuggestions(prev => { const next = { ...prev }; delete next[key]; return next; });
  };

  // Load section comment counts for all categories
  const loadCommentCounts = useCallback(async (categories: string[], qId: string) => {
    const counts: { [category: string]: number } = {};
    for (const cat of categories) {
      try {
        const data = await apiClient.getSectionComments(qId, cat);
        counts[cat] = data.comments.length;
      } catch {
        counts[cat] = 0;
      }
    }
    setCommentCounts(counts);
  }, []);

  // Load customer if not passed
  useEffect(() => {
    if (!customer && customerId) {
      loadCustomer();
    }
  }, [customerId]);

  const loadCustomer = async () => {
    try {
      const data = await apiClient.getCustomer(customerId!);
      setCustomer(data);
    } catch (error) {
      console.error('Error loading customer:', error);
    }
  };

  // Load questionnaire and response
  useEffect(() => {
    if (responseId) {
      loadExistingResponse();
    } else if (questionnaireId) {
      loadQuestionnaire();
    }
  }, [questionnaireId, responseId]);

  // Auto-save every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (questionnaire && Object.keys(responses).length > 0) {
        handleSave(true);
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [questionnaire, responses]);

  const loadQuestionnaire = async () => {
    try {
      const data = await apiClient.getQuestionnaire(questionnaireId!);
      console.log('Loaded questionnaire:', data);
      console.log('First question:', data.questions[0]);
      setQuestionnaire(data);

      // Initialize all categories as expanded
      const expanded: { [key: string]: boolean } = {};
      data.categories.forEach(cat => {
        expanded[cat] = true;
      });
      setExpandedCategories(expanded);
      loadCommentCounts(data.categories, data.questionnaireId);
    } catch (error) {
      console.error('Error loading questionnaire:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadExistingResponse = async () => {
    try {
      // Load the response data
      const responseData = await apiClient.getResponse(responseId!, customerId!);
      console.log('Loaded response:', responseData);
      
      // Load the questionnaire
      const questionnaireData = await apiClient.getQuestionnaire(responseData.questionnaireId);
      setQuestionnaire(questionnaireData);
      
      // Convert response array to dictionary format
      const responsesDict: { [key: string]: string } = {};
      const savedAiSuggestions: { [key: string]: string[] } = {};
      if (responseData.responses) {
        responseData.responses.forEach(r => {
          const key = getQuestionKey(r.category, r.question);
          responsesDict[key] = r.customerAnswer || '';
          if (r.aiSuggestions?.length) {
            savedAiSuggestions[key] = r.aiSuggestions;
          }
        });
      }
      setResponses(responsesDict);
      if (Object.keys(savedAiSuggestions).length > 0) {
        setAiSuggestions(savedAiSuggestions);
      }
      
      // Initialize all categories as expanded
      const expanded: { [key: string]: boolean } = {};
      questionnaireData.categories.forEach(cat => {
        expanded[cat] = true;
      });
      setExpandedCategories(expanded);
      loadCommentCounts(questionnaireData.categories, questionnaireData.questionnaireId);

      // Set last saved time
      if (responseData.updatedAt) {
        setLastSaved(new Date(responseData.updatedAt));
      }
    } catch (error) {
      console.error('Error loading response:', error);
    } finally {
      setLoading(false);
    }
  };

  const getQuestionKey = (category: string, question: string) => {
    return `${category}::${question}`;
  };


  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const handleSave = async (isAutoSave = false) => {
    if (!questionnaire || !customer) {
      console.log('Cannot save: missing questionnaire or customer', { questionnaire, customer });
      return;
    }

    console.log('Starting save...', { isAutoSave, customerId, questionnaireId: questionnaireId || questionnaire.questionnaireId, responseId });
    setSaving(true);
    try {
      // Build responses array (include AI suggestions if available)
      const responseArray: QuestionResponse[] = questionnaire.questions.map(q => {
        const key = getQuestionKey(q.category, q.question);
        return {
          category: q.category,
          question: q.question,
          defaultAnswer: q.defaultAnswer,
          customerAnswer: responses[key] || '',
          notes: q.notes,
          ...(aiSuggestions[key]?.length ? { aiSuggestions: aiSuggestions[key] } : {})
        };
      });

      console.log('Saving response with', responseArray.length, 'questions');
      
      let result;
      if (responseId) {
        // Update existing response
        result = await apiClient.updateResponse(responseId, customerId!, {
          customerId: customerId!,
          customerName: customer.customerName,
          questionnaireId: questionnaire.questionnaireId,
          responses: responseArray
        });
      } else {
        // Create new response
        result = await apiClient.createResponse({
          customerId: customerId!,
          customerName: customer.customerName,
          questionnaireId: questionnaireId!,
          responses: responseArray
        });
      }

      console.log('Save successful:', result);
      setLastSaved(new Date());
      
      if (!isAutoSave) {
        alert(t('questionnaire', 'saved'));
        navigate(`/customer/${customerId}`);
      }
    } catch (error) {
      console.error('Error saving response:', error);
      if (!isAutoSave) {
        alert(t('questionnaire', 'saveFailed'));
      }
    } finally {
      setSaving(false);
    }
  };

  const calculateProgress = () => {
    if (!questionnaire) return 0;
    const totalQuestions = questionnaire.questions.length;
    const answeredQuestions = questionnaire.questions.filter(q => 
      responses[getQuestionKey(q.category, q.question)]?.trim()
    ).length;
    return (answeredQuestions / totalQuestions * 100).toFixed(1);
  };

  if (loading) {
    return <div style={{ padding: '20px' }}>Loading...</div>;
  }

  if (!questionnaire) {
    return <div style={{ padding: '20px' }}>Questionnaire not found</div>;
  }

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px' }}>
      {/* Header */}
      <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <button
          onClick={() => navigate(`/customer/${customerId}`)}
          style={{
            padding: '8px 16px',
            backgroundColor: '#6c757d',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          {t('common', 'back')}
        </button>
        <div style={{ fontSize: '14px', color: '#666' }}>
          {lastSaved && `${t('questionnaire', 'lastSaved')} ${lastSaved.toLocaleTimeString('ja-JP')}`}
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '5px' }}>
        <h2 style={{ margin: 0 }}>{questionnaire.title}</h2>
        <div style={{ display: 'flex', borderRadius: '14px', overflow: 'hidden', border: '2px solid #2563eb' }}>
          <button
            onClick={() => { setContentLang('ja'); try { localStorage.setItem('ea-qa-content-language', 'ja'); } catch {} }}
            style={{
              padding: '4px 10px',
              fontSize: '12px',
              fontWeight: 700,
              backgroundColor: contentLang === 'ja' ? '#2563eb' : '#fff',
              color: contentLang === 'ja' ? '#fff' : '#2563eb',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            JP
          </button>
          <button
            onClick={() => { setContentLang('en'); try { localStorage.setItem('ea-qa-content-language', 'en'); } catch {} }}
            style={{
              padding: '4px 10px',
              fontSize: '12px',
              fontWeight: 700,
              backgroundColor: contentLang === 'en' ? '#2563eb' : '#fff',
              color: contentLang === 'en' ? '#fff' : '#2563eb',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            EN
          </button>
        </div>
      </div>
      <p>{customer?.customerName}</p>

      {/* Progress Bar */}
      <div style={{
        backgroundColor: '#f5f5f5',
        padding: '15px',
        borderRadius: '8px',
        marginBottom: '20px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
          <span>{t('questionnaire', 'progress')}</span>
          <span>{calculateProgress()}%</span>
        </div>
        <div style={{
          height: '20px',
          backgroundColor: '#e9ecef',
          borderRadius: '10px',
          overflow: 'hidden'
        }}>
          <div style={{
            height: '100%',
            width: `${calculateProgress()}%`,
            backgroundColor: '#28a745',
            transition: 'width 0.3s'
          }} />
        </div>
      </div>

      {/* Questions by Category */}
      {questionnaire.categories.map((category) => (
        <div
          key={category}
          style={{
            backgroundColor: '#fff',
            marginBottom: '20px',
            borderRadius: '8px',
            border: '1px solid #ddd',
            overflow: 'hidden'
          }}
        >
          {/* Category Header */}
          <div
            style={{
              backgroundColor: '#007bff',
              color: '#fff',
              padding: '15px',
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <div onClick={() => toggleCategory(category)} style={{ flex: 1 }}>
              <h3 style={{ margin: 0 }}>{category}</h3>
              {contentLang === 'en' && questionnaire.questionsByCategory[category]?.[0]?.categoryEn && (
                <div style={{ fontSize: '13px', opacity: 0.85, marginTop: '2px' }}>
                  {questionnaire.questionsByCategory[category][0].categoryEn}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <button
                onClick={(e) => { e.stopPropagation(); setCommentPopupCategory(category); }}
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  border: '1px solid rgba(255,255,255,0.4)',
                  borderRadius: '14px',
                  padding: '4px 10px',
                  color: '#fff',
                  cursor: 'pointer',
                  fontSize: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
                title={t('sectionComments', 'title')}
              >
                💬 {commentCounts[category] ? commentCounts[category] : ''}
              </button>
              <span onClick={() => toggleCategory(category)}>{expandedCategories[category] ? '▼' : '▶'}</span>
            </div>
          </div>

          {/* Questions */}
          {expandedCategories[category] && (
            <div style={{ padding: '20px' }}>
              {questionnaire.questionsByCategory[category]?.map((question, idx) => (
                <div
                  key={idx}
                  style={{
                    marginBottom: '30px',
                    paddingBottom: '30px',
                    borderBottom: idx < questionnaire.questionsByCategory[category].length - 1 ? '1px solid #eee' : 'none'
                  }}
                >
                  {/* Question */}
                  <div style={{ marginBottom: '10px' }}>
                    <div style={{ fontWeight: 'bold', fontSize: '16px' }}>
                      Q{idx + 1}. {question.question}
                    </div>
                    {contentLang === 'en' && question.questionEn && (
                      <div style={{ fontSize: '14px', color: '#2563eb', marginTop: '3px', paddingLeft: '28px' }}>
                        {question.questionEn}
                      </div>
                    )}
                  </div>

                  {/* Default Answer */}
                  {question.defaultAnswer && (
                    <div style={{
                      backgroundColor: '#f8f9fa',
                      padding: '10px',
                      borderRadius: '4px',
                      marginBottom: '10px',
                      fontSize: '14px'
                    }}>
                      <div style={{ color: '#666', marginBottom: '5px', fontSize: '12px' }}>
                        {t('questionnaire', 'defaultAnswer')}
                      </div>
                      <div>{question.defaultAnswer}</div>
                      {contentLang === 'en' && question.defaultAnswerEn && (
                        <div style={{ color: '#2563eb', marginTop: '4px', fontSize: '13px' }}>
                          {question.defaultAnswerEn}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Customer Answer with AI */}
                  <div style={{ position: 'relative' }}>
                    <textarea
                      value={responses[getQuestionKey(category, question.question)] || ''}
                      onChange={(e) => handleAnswerChangeWithSuggestions(category, question.question, question.defaultAnswer || '', e.target.value)}
                      placeholder={t('questionnaire', 'answerPlaceholder')}
                      style={{
                        width: '100%',
                        minHeight: '80px',
                        padding: '10px',
                        paddingRight: '50px',
                        fontSize: '14px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontFamily: 'inherit',
                        resize: 'vertical'
                      }}
                    />
                    {/* AI Regenerate Button */}
                    <button
                      onClick={() => handleAiRegenerate(category, question.question, question.defaultAnswer || '')}
                      disabled={aiLoading[getQuestionKey(category, question.question)] || !responses[getQuestionKey(category, question.question)]?.trim()}
                      title={t('questionnaire', 'aiReformat')}
                      style={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        padding: '4px 8px',
                        fontSize: '11px',
                        fontWeight: 600,
                        backgroundColor: aiLoading[getQuestionKey(category, question.question)] ? '#93c5fd' : '#3b82f6',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: aiLoading[getQuestionKey(category, question.question)] ? 'wait' : 'pointer',
                        opacity: !responses[getQuestionKey(category, question.question)]?.trim() ? 0.4 : 1,
                      }}
                    >
                      {aiLoading[getQuestionKey(category, question.question)] ? 'AI...' : 'AI'}
                    </button>
                  </div>

                  {/* AI Suggestions */}
                  {suggestLoading[getQuestionKey(category, question.question)] && (
                    <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ display: 'inline-block', width: '12px', height: '12px', border: '2px solid #d1d5db', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} />
                      {t('questionnaire', 'aiSuggesting')}
                    </div>
                  )}
                  {aiSuggestions[getQuestionKey(category, question.question)] && (
                    <div style={{
                      marginTop: '8px',
                      padding: '10px',
                      backgroundColor: '#eff6ff',
                      border: '1px solid #bfdbfe',
                      borderRadius: '6px',
                    }}>
                      <div style={{ fontSize: '11px', fontWeight: 600, color: '#2563eb', marginBottom: '6px' }}>
                        {t('questionnaire', 'aiSuggestions')}
                      </div>
                      {aiSuggestions[getQuestionKey(category, question.question)].map((suggestion, sIdx) => (
                        <div
                          key={sIdx}
                          onClick={() => applySuggestion(category, question.question, suggestion)}
                          style={{
                            padding: '6px 10px',
                            marginBottom: sIdx < aiSuggestions[getQuestionKey(category, question.question)].length - 1 ? '4px' : 0,
                            backgroundColor: '#fff',
                            border: '1px solid #dbeafe',
                            borderRadius: '4px',
                            fontSize: '13px',
                            color: '#1e40af',
                            cursor: 'pointer',
                            transition: 'background-color 0.15s',
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#dbeafe')}
                          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#fff')}
                        >
                          {suggestion}
                        </div>
                      ))}
                      <button
                        onClick={() => setAiSuggestions(prev => { const next = { ...prev }; delete next[getQuestionKey(category, question.question)]; return next; })}
                        style={{ marginTop: '6px', fontSize: '11px', color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                      >
                        {t('common', 'close')}
                      </button>
                    </div>
                  )}

                  {/* Notes */}
                  {question.notes && (
                    <div style={{
                      fontSize: '12px',
                      color: '#666',
                      marginTop: '5px'
                    }}>
                      <span>{t('questionnaire', 'notes')} {question.notes}</span>
                      {contentLang === 'en' && question.notesEn && (
                        <span style={{ color: '#2563eb', marginLeft: '8px' }}>({question.notesEn})</span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}

      {/* Save Button */}
      <div style={{ textAlign: 'center', marginTop: '30px' }}>
        <button
          onClick={() => handleSave(false)}
          disabled={saving}
          style={{
            padding: '15px 40px',
            backgroundColor: '#28a745',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            cursor: saving ? 'not-allowed' : 'pointer',
            fontSize: '18px',
            fontWeight: 'bold'
          }}
        >
          {saving ? t('common', 'saving') : t('questionnaire', 'saveAndComplete')}
        </button>
      </div>

      {/* Section Comment Popup */}
      {commentPopupCategory && questionnaire && (
        <SectionCommentPopup
          questionnaireId={questionnaire.questionnaireId}
          category={commentPopupCategory}
          questions={(questionnaire.questionsByCategory[commentPopupCategory] || []).map(q => q.question)}
          onClose={() => setCommentPopupCategory(null)}
          onCommentCountChange={(count) => setCommentCounts(prev => ({ ...prev, [commentPopupCategory]: count }))}
        />
      )}
    </div>
  );
};

export default Questionnaire;
